import axios, {
  // eslint-disable-next-line no-unused-vars
  Method, AxiosRequestConfig,
} from "axios";

import store from "../store/index";
import { setNotAuthenticated, setAccessToken } from "../store/actions";

// eslint-disable-next-line no-unused-vars
import { AccessTokenMessage } from "./AccessTokenMessage";
// eslint-disable-next-line no-unused-vars
import { ReduxState } from "../store/ReduxState";

export const checkHttpStatus = (error: any, status: number): boolean => {
  if (error?.response?.status == null) {
    return false;
  }

  return Number(error.response.status) === status;
};

const refreshToken = async ():Promise<AccessTokenMessage> => {
  const { dispatch } = store;
  const refreshedToken = await axios.get<AccessTokenMessage>("/api/v1/token/refresh");

  dispatch(setAccessToken(
    refreshedToken.data.accessToken,
    new Date(refreshedToken.data.expiresAt),
    new Date()
  ));

  return refreshedToken.data;
};

const getAccessToken = async () => {
  const state : ReduxState = store.getState();
  const { dispatch } = store;
  let {
    accessToken,
    tokenExpirationDate,
    tokenCreationDate,
  } = state.authentication;

  try {
    if (state.authentication.accessToken == null) {
      // initially load the token
      const result = await axios.get<AccessTokenMessage>("/api/v1/token");
      dispatch(setAccessToken(
        result.data.accessToken,
        new Date(result.data.expiresAt),
        new Date()
      ));
      accessToken = result.data.accessToken;
      tokenExpirationDate = new Date(result.data.expiresAt);
      tokenCreationDate = new Date();
    }

    // check if we are half way to expiration
    const remainingTime = tokenExpirationDate.getTime() - new Date().getTime();
    const totalWindow = tokenExpirationDate.getTime() - tokenCreationDate.getTime();

    if (remainingTime < 0 || remainingTime < totalWindow / 2) {
      // refresh token
      const data = await refreshToken();
      accessToken = data.accessToken;
      tokenExpirationDate = new Date(data.expiresAt);
      tokenCreationDate = new Date();
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Failed to get token", error);
    return "";
  }

  return accessToken;
};

const unAuthenticatedHandler = async <TData, TResponse>(
  url: string,
  method: Method,
  data?: TData
): Promise<TResponse> => {
  const retryAction = new Promise<TResponse>((resolve, reject) => {
    const retryApi = async () => {
      try {
        // eslint-disable-next-line no-use-before-define
        const result = await makeApiRequestInternal<TData, TResponse>(
          url, method, data, undefined, false
        );
        resolve(result);
      } catch (error) {
        reject(error);
      }
    };

    store.dispatch(setNotAuthenticated(retryApi));
  });

  // first try to refresh the token
  try {
    await refreshToken();
  } catch (error) {
    // refresh token failed, set as unauthenticated
    return retryAction;
  }

  // token was refreshed, now call API again
  try {
    // eslint-disable-next-line no-use-before-define
    const result = await makeApiRequestInternal<TData, TResponse>(
      url,
      method,
      data,
      undefined,
      false
    );
    return result;
  } catch (error) {
    if (checkHttpStatus(error, 404)) {
      // failed even after token refresh. Set unauthenticated
      return retryAction;
    }
    throw error;
  }
};

const makeApiRequestInternal = async <TData, TResponse>(
  url: string,
  method: Method,
  data?: TData,
  requestConfig: AxiosRequestConfig = {},
  retryOnAuthFailure: boolean = true): Promise<TResponse> => {
  let retry = false;
  let lastError = null;
  try {
    let relativeUrl = url;
    if (!relativeUrl.startsWith("/")) {
      relativeUrl = `/${url}`;
    }
    const apiUrl = `/api/v1${relativeUrl}`;
    const token = await getAccessToken();

    const config : AxiosRequestConfig = {
      ...requestConfig,
      headers: { Authorization: `Bearer ${token}` },
      withCredentials: false,
      method,
      data,
      url: apiUrl,
    };

    const result = await axios.request<TResponse>(config);
    return result.data;
    // return result.data;
  } catch (error) {
    lastError = error;
    if (retryOnAuthFailure && checkHttpStatus(error, 401)) {
      // not authenticated. Re-authenticate and try again
      retry = true;
    }
  }

  if (retry) {
    const result = await unAuthenticatedHandler<TData, TResponse>(url, method, data);
    return result;
  }

  throw lastError;
};

export const makeApiRequest = async <TData, TResponse>(
  url: string,
  method: Method,
  data?: TData,
  requestConfig?: AxiosRequestConfig,
  retryOnAuthFailure?: boolean
): Promise<TResponse> => makeApiRequestInternal(
  url,
  method,
  data,
  requestConfig,
  retryOnAuthFailure
);
